/**
 * Behavior Service
 * 
 * API abstraction layer for behavior management endpoints.
 * All methods handle encryption/decryption transparently.
 */

import apiAxiosInstance from '../utils/axiosInstance';

// ═══════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════

export interface BehaviorCase {
  _id: string;
  teacherId: string;
  title?: string;
  scope: {
    type: 'student' | 'group';
    studentId?: string;
    groupId?: string;
  };
  status: 'active' | 'monitoring' | 'closed';
  tier: 1 | 2 | 3;
  targetBehaviors: Array<{
    label: string;
    definition?: string;
  }>;
  goals: Array<{
    description: string;
    metric: 'frequency' | 'duration' | 'latency' | 'percentage';
    target: number;
    period: 'daily' | 'weekly' | 'per_class';
    dueDate?: string;
  }>;
  hypotheses?: {
    functionOfBehavior?: 'attention' | 'escape' | 'tangible' | 'sensory';
    triggers?: string[];
    settingEvents?: string[];
  };
  stats?: {
    totalNotes: number;
    totalIncidents: number;
    lastIncidentAt?: string;
    activeStrategies: number;
  };
  lastMessageAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BehaviorNote {
  _id: string;
  caseId: string;
  teacherId: string;
  type:
    | 'abc'
    | 'observation'
    | 'strategy_summary'
    | 'guardian_contact'
    | 'ai_summary'
    | 'incident'
    | 'chat_message'
    | 'conversation_summary'
    | 'other';
  senderRole?: 'user' | 'assistant';
  keyPoints?: string[];
  content: string;
  abc?: {
    antecedent: string;
    behavior: string;
    consequence: string;
    setting?: string;
    intensity?: 'low' | 'moderate' | 'high';
  };
  sentiment?: 'positive' | 'neutral' | 'negative';
  createdAt: string;
  updatedAt: string;
}

export interface BehaviorIncident {
  _id: string;
  caseId: string;
  teacherId: string;
  studentId: string;
  incidentType: 'disruption' | 'aggression' | 'off_task' | 'defiance' | 'emotional_dysregulation' | 'other';
  severity: 'low' | 'moderate' | 'high' | 'crisis';
  description: string;
  location?: 'classroom' | 'hallway' | 'cafeteria' | 'playground' | 'library' | 'bathroom' | 'other';
  timeOfDay?: 'morning' | 'mid_morning' | 'lunch' | 'afternoon' | 'end_of_day';
  subjectArea?: string;
  antecedent?: string;
  consequence?: string;
  staffResponse?: string;
  resolution?: string;
  followUpNeeded?: boolean;
  duration?: number;
  peersInvolved?: string[];
  incidentAt: string;
  createdAt: string;
}

export interface CreateIncidentInput {
  incidentType: BehaviorIncident['incidentType'];
  severity: BehaviorIncident['severity'];
  description: string;
  location?: BehaviorIncident['location'];
  timeOfDay?: BehaviorIncident['timeOfDay'];
  subjectArea?: string;
  antecedent?: string;
  consequence?: string;
  staffResponse?: string;
  resolution?: string;
  followUpNeeded?: boolean;
  duration?: number;
  incidentAt?: string;
}

export interface StrategyAttempt {
  _id: string;
  caseId: string;
  teacherId: string;
  strategy: {
    id?: string;
    name: string;
    description?: string;
    tier?: 1 | 2 | 3;
  };
  startedAt: string;
  endedAt?: string;
  outcomeRating?: 1 | 2 | 3 | 4 | 5;
  outcomeNotes?: string;
  status: 'active' | 'completed' | 'discontinued';
}

export interface StrategyLibraryItem {
  _id: string;
  name: string;
  tier: 1 | 2 | 3;
  tags: string[];
  description: string;
  whenToUse?: string;
  howToImplement?: string;
  cautions?: string;
  dataCollection?: string;
}

export interface IncidentSummary {
  total: number;
  byType: Record<string, number>;
  bySeverity: Record<string, number>;
  byLocation: Record<string, number>;
  trend: Array<{
    date: string;
    count: number;
  }>;
}

// ═══════════════════════════════════════════════════════════
// SERVICE METHODS
// ═══════════════════════════════════════════════════════════

export const behaviorService = {
  // ═══════════════════════════════════════════════════════
  // CASES
  // ═══════════════════════════════════════════════════════
  
  async getCases(params?: { 
    scope?: string; 
    studentId?: string; 
    groupId?: string;
    status?: string 
  }): Promise<BehaviorCase[]> {
    const response = await apiAxiosInstance.get('/api/behavior/cases', { params });
    return response.data;
  },

  async getCase(id: string): Promise<BehaviorCase> {
    const response = await apiAxiosInstance.get(`/api/behavior/cases/${id}`);
    return response.data;
  },

  async createCase(data: Partial<BehaviorCase>): Promise<BehaviorCase> {
    const response = await apiAxiosInstance.post('/api/behavior/cases', data);
    return response.data;
  },

  async updateCase(id: string, data: Partial<BehaviorCase>): Promise<BehaviorCase> {
    const response = await apiAxiosInstance.patch(`/api/behavior/cases/${id}`, data);
    return response.data;
  },

  // ═══════════════════════════════════════════════════════
  // NOTES
  // ═══════════════════════════════════════════════════════
  
  async getNotes(caseId: string): Promise<BehaviorNote[]> {
    const response = await apiAxiosInstance.get(`/api/behavior/cases/${caseId}/notes`);
    return response.data;
  },

  async createNote(caseId: string, data: {
    type: BehaviorNote['type'];
    content?: string;
    abc?: BehaviorNote['abc'];
    sentiment?: BehaviorNote['sentiment'];
  }): Promise<BehaviorNote> {
    const response = await apiAxiosInstance.post(`/api/behavior/cases/${caseId}/notes`, data);
    return response.data;
  },

  // ═══════════════════════════════════════════════════════
  // INCIDENTS
  // ═══════════════════════════════════════════════════════
  
  async getIncidents(
    caseId: string, 
    options?: { limit?: number; skip?: number }
  ): Promise<BehaviorIncident[]> {
    const response = await apiAxiosInstance.get(
      `/api/behavior/cases/${caseId}/incidents`,
      { params: options }
    );
    return response.data;
  },

  async createIncident(
    caseId: string, 
    data: CreateIncidentInput
  ): Promise<BehaviorIncident> {
    const response = await apiAxiosInstance.post(
      `/api/behavior/cases/${caseId}/incidents`, 
      data
    );
    return response.data;
  },

  async getIncidentSummary(
    caseId: string, 
    days: number = 30
  ): Promise<IncidentSummary> {
    const response = await apiAxiosInstance.get(
      `/api/behavior/cases/${caseId}/incidents/summary`,
      { params: { days } }
    );
    return response.data;
  },

  // ═══════════════════════════════════════════════════════
  // STRATEGIES
  // ═══════════════════════════════════════════════════════
  
  async getStrategies(caseId: string): Promise<StrategyAttempt[]> {
    const response = await apiAxiosInstance.get(
      `/api/behavior/cases/${caseId}/strategies`
    );
    return response.data;
  },

  async createStrategyAttempt(
    caseId: string, 
    data: {
      strategy: StrategyAttempt['strategy'];
      startedAt?: string;
    }
  ): Promise<StrategyAttempt> {
    const response = await apiAxiosInstance.post(
      `/api/behavior/cases/${caseId}/strategies`, 
      data
    );
    return response.data;
  },

  async updateStrategyAttempt(
    strategyId: string,
    data: Partial<StrategyAttempt>
  ): Promise<StrategyAttempt> {
    const response = await apiAxiosInstance.patch(
      `/api/behavior/strategies/${strategyId}`, 
      data
    );
    return response.data;
  },

  async getStrategyLibrary(
    query?: string, 
    tier?: 1 | 2 | 3
  ): Promise<StrategyLibraryItem[]> {
    const response = await apiAxiosInstance.get('/api/behavior/strategy-library', {
      params: { query, tier }
    });
    return response.data;
  }
};

export default behaviorService;
